import { BRSR_METHODOLOGY_VERSION, bandForScore } from '../../config/brsrConfig'
import { REGULATORY_CONFIG_VERSION } from '../../data/brsr/regulatoryRules'
import { BRSR_QUESTIONS } from '../../data/brsr/questions'
import type { AnswerMap, BrsrResult, BrsrState } from '../../data/brsr/types'
import { evaluateApplicability } from './applicability'
import { buildActions, buildGaps, buildReviewItems, buildStrengths, keyFindings } from './recommendations'
import {
  completionStats,
  isProvisional,
  overallFromDimensions,
  scoreDimensions,
  scorePrinciples,
  scoreSections,
} from './scoring'
import { scalarAnswer } from './labels'
import { isQuestionVisible } from './visibility'

export function createInitialBrsrState(): BrsrState {
  return {
    methodologyVersion: BRSR_METHODOLOGY_VERSION,
    regulatoryConfigVersion: REGULATORY_CONFIG_VERSION,
    currentStep: 'profile',
    responses: {},
    result: null,
    completed: false,
    startedAt: new Date().toISOString(),
  }
}

export function computeBrsrResult(responses: AnswerMap, assessedAt = new Date().toISOString()): BrsrResult {
  const applicability = evaluateApplicability(responses)
  const dimensions = scoreDimensions(responses)
  const stats = completionStats(responses)
  const requiredVisible = BRSR_QUESTIONS.filter((question) => question.required && isQuestionVisible(question, responses)).length
  const score = overallFromDimensions(dimensions)
  const provisional = score == null || isProvisional(dimensions, stats, requiredVisible)
  const band = score == null ? null : bandForScore(score)
  const gaps = buildGaps(responses, applicability)
  const reviewItems = buildReviewItems(responses)
  const strengths = buildStrengths(responses)
  const actions = buildActions(gaps, reviewItems)
  const company = scalarAnswer(responses, 'company_name').trim()

  return {
    methodologyVersion: BRSR_METHODOLOGY_VERSION,
    regulatoryConfigVersion: REGULATORY_CONFIG_VERSION,
    assessedAt,
    companyName: company || 'Not provided',
    financialYearLabel: applicability.financialYearLabel,
    naExplanation: scalarAnswer(responses, 'na_explanation').trim(),
    applicability,
    dimensions,
    principleScores: scorePrinciples(responses),
    sectionScores: scoreSections(responses),
    overall: {
      score,
      provisional,
      bandId: band?.id ?? null,
      bandLabel: score == null ? 'Not enough answered questions to calculate a score' : provisional ? `Provisional — ${band?.label}` : band?.label ?? '',
      completion: stats.completion,
      unanswered: stats.unanswered,
      unknown: stats.unknown,
      criticalGaps: gaps.filter((gap) => gap.priority === 'p1').length,
      reviewItems: reviewItems.length,
    },
    gaps,
    reviewItems,
    strengths,
    actions,
    keyFindings: keyFindings(gaps, reviewItems),
  }
}
